import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Heart, ShoppingCart, Check, AlertTriangle, Package, Plus, Settings } from 'lucide-react';
import toast from 'react-hot-toast';
import { usePicks } from '../../context/PicksContext';
import { useCart } from '../../context/CartContext';
import { SharedPickResolution } from '../../types';
import { MAX_PICKS } from '../../utils/picksShare';

export function SharedPicksModal() {
  const { state, closeSharedModal, addPick, totalPicks, requestConfigure } = usePicks();
  const { addItem } = useCart();
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());

  const shared = state.shared;
  if (!shared) return null;

  const handleAddOne = (item: SharedPickResolution) => {
    if (item.status !== 'ok' || !item.product || !item.variant) return;
    addItem(item.product, item.variant);
    setAddedIds((prev) => new Set(prev).add(item.sharedId));
    toast.success('Added to cart', { icon: '🛒' });
    setTimeout(() => {
      setAddedIds((prev) => { const n = new Set(prev); n.delete(item.sharedId); return n; });
    }, 1500);
  };

  const handleAddAll = () => {
    const valid = shared.items.filter((i) => i.status === 'ok' && i.product && i.variant);
    valid.forEach((i) => {
      if (i.product && i.variant) addItem(i.product, i.variant);
    });
    toast.success(`${valid.length} item${valid.length !== 1 ? 's' : ''} added to cart`, { icon: '🛒' });
    closeSharedModal();
  };

  const handleSaveAllToMyPicks = () => {
    const valid = shared.items.filter((i) => (i.status === 'ok' || i.status === 'bare') && i.product);
    let added = 0;
    let skipped = 0;
    for (const i of valid) {
      if (totalPicks + added >= MAX_PICKS) { skipped += 1; continue; }
      if (i.product) {
        const ok = addPick(i.product, i.variant || null, i.selections || {});
        if (ok) added += 1;
      }
    }
    if (added > 0) {
      toast.success(`${added} saved to My Picks`, { icon: '♥' });
    }
    if (skipped > 0) {
      toast(`${skipped} skipped — My Picks is full`, { icon: '⚠️' });
    }
  };

  const okCount = shared.items.filter((i) => i.status === 'ok').length;
  const bareCount = shared.items.filter((i) => i.status === 'bare').length;
  const validCount = okCount + bareCount;
  const invalidCount = shared.items.length - validCount;

  return (
    <AnimatePresence>
      {shared.showModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={closeSharedModal}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-2xl max-h-[92vh] sm:max-h-[85vh] overflow-hidden shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-[#7B68CC]/10 to-transparent">
              <div className="flex items-center gap-2.5">
                <Heart size={20} className="text-[#7B68CC]" fill="currentColor" />
                <div>
                  <h2 className="font-bold text-ndms-navy text-lg leading-tight">{shared.isReorder ? 'Reorder Your Items' : 'Shared Picks'}</h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {validCount} available{invalidCount > 0 ? `, ${invalidCount} unavailable` : ''}
                  </p>
                </div>
              </div>
              <button
                onClick={closeSharedModal}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto scrollbar-thin px-5 py-4 space-y-3">
              {shared.items.map((item) => {
                // Bare pick — product exists but no variant info. Offer to configure.
                if (item.status === 'bare' && item.product) {
                  return (
                    <motion.div
                      key={item.sharedId}
                      layout
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex gap-3 bg-[#7B68CC]/5 border border-[#7B68CC]/20 rounded-2xl p-3"
                    >
                      <div className="w-16 h-16 bg-ndms-cream rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {item.product.imageUrl ? (
                          <img src={item.product.imageUrl} alt={item.product.name} className="w-full h-full object-cover" />
                        ) : (
                          <Package size={20} className="text-ndms-navy/30" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-[#7B68CC] mb-0.5">{item.product.brand}</div>
                        <div className="text-sm font-semibold text-gray-900 leading-tight truncate">{item.product.name}</div>
                        <div className="text-xs text-gray-500 mt-0.5">Tap to pick color &amp; size</div>
                        <button
                          onClick={() => {
                            if (item.product) {
                              requestConfigure(item.product, item.selections || {});
                              closeSharedModal();
                            }
                          }}
                          className="mt-2 flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-white border border-[#7B68CC] text-[#7B68CC] hover:bg-[#7B68CC] hover:text-white transition-colors"
                        >
                          <Settings size={12} /> Configure
                        </button>
                      </div>
                    </motion.div>
                  );
                }
                if (item.status !== 'ok' || !item.product || !item.variant) {
                  return (
                    <div key={item.sharedId} className="flex gap-3 bg-gray-50 rounded-2xl p-3 opacity-75">
                      <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <AlertTriangle size={20} className="text-amber-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-amber-600 mb-0.5">
                          {item.status === 'missing' ? 'Product no longer available' : 'Variation no longer available'}
                        </div>
                        <div className="text-sm text-gray-500 truncate">
                          {item.product?.name || item.raw.p}
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          {[item.raw.c, item.raw.l && `${item.raw.l} mmHg`, item.raw.z && `Size ${item.raw.z}`].filter(Boolean).join(', ')}
                        </div>
                      </div>
                    </div>
                  );
                }
                const added = addedIds.has(item.sharedId);
                return (
                  <motion.div
                    key={item.sharedId}
                    layout
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex gap-3 bg-gray-50 rounded-2xl p-3"
                  >
                    <div className="w-16 h-16 bg-ndms-cream rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {item.product.imageUrl ? (
                        <img src={item.product.imageUrl} alt={item.product.name} className="w-full h-full object-cover" />
                      ) : (
                        <Package size={20} className="text-ndms-navy/30" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-[#7B68CC] mb-0.5">{item.product.brand}</div>
                      <div className="text-sm font-semibold text-gray-900 leading-tight truncate">{item.product.name}</div>
                      {item.variantDescription && (
                        <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{item.variantDescription}</div>
                      )}
                      <button
                        onClick={() => handleAddOne(item)}
                        disabled={added}
                        className={`mt-2 flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-all ${
                          added
                            ? 'bg-green-500 text-white'
                            : 'bg-ndms-navy text-white hover:bg-[#7B68CC]'
                        }`}
                      >
                        {added ? (
                          <><Check size={12} /> Added</>
                        ) : (
                          <><ShoppingCart size={12} /> Add to my cart</>
                        )}
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Footer */}
            {validCount > 0 && (
              <div className="border-t border-gray-100 px-5 py-3 space-y-2 bg-gray-50">
                {okCount > 0 && (
                  <motion.button
                    whileTap={{ scale: 0.97 }}
                    onClick={handleAddAll}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-sm bg-ndms-navy text-white hover:bg-[#7B68CC] transition-colors"
                  >
                    <ShoppingCart size={16} />
                    Add {okCount === shared.items.length ? 'all' : `${okCount}`} to cart
                  </motion.button>
                )}
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  onClick={handleSaveAllToMyPicks}
                  className="w-full flex items-center justify-center gap-2 py-2 rounded-xl font-semibold text-xs text-[#7B68CC] hover:bg-[#7B68CC]/5 transition-colors"
                >
                  <Plus size={14} />
                  Save these to my picks
                </motion.button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
